import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Search } from "lucide-react";
import { useState } from "react";
import type {
  CreditNote,
  CreditNoteReason,
  Invoice,
  WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatMoney } from "@/modules/gst/format";
import { CREDIT_NOTE_REASONS } from "@/modules/gst/reasons";

/**
 * Per-line credited quantity, keyed by `InvoiceLine.id`.
 *
 * Seeded to each line's full original quantity when an invoice is picked —
 * a full-invoice credit note (`order_cancelled`, `full_refund`) is the
 * common case, and starting every row at zero would make the ordinary path
 * the one that takes more clicks.
 */
type LineQuantities = Record<string, number>;

function fullQuantities(invoice: Invoice): LineQuantities {
  const next: LineQuantities = {};
  for (const line of invoice.lines) next[line.id] = line.quantity;
  return next;
}

/**
 * Raise a credit note against an issued invoice (FR-GST-16, ticket #91).
 *
 * Two steps in one screen rather than a wizard: find the invoice, then fill
 * in the reason and the per-line quantities. Nothing here recalculates a
 * tax split — `taxKind` and every rate shown below are read straight off
 * `selectedInvoice`, never off today's HSN master, because a credit note
 * reverses the specific charge that invoice made and what the rate is today
 * is irrelevant to what was actually charged then. The only number this
 * form lets someone choose is a credited quantity per line; the amounts
 * that quantity produces are computed by the write, not previewed here —
 * see the comment on `LinesTable` for why.
 */
export function CreateCreditNote({
  onCreated,
  onCancel,
}: {
  onCreated: (note: CreditNote) => void;
  onCancel: () => void;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  // A plain `string` rather than `CreditNoteReason | ""` so the `Select`
  // below needs no cast on every keystroke — the union is enforced once, at
  // submit time, by looking the typed value up in `CREDIT_NOTE_REASONS`.
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [quantities, setQuantities] = useState<LineQuantities>({});
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const searchQuery = useQuery({
    queryKey: ["gst", "invoice-search-for-credit-note", search],
    queryFn: () => data.invoicing.list({ search, limit: 8 }),
    enabled: selectedInvoice === null && search.trim().length > 0,
  });

  const mutation = useMutation({
    mutationFn: (input: {
      invoiceId: string;
      reason: CreditNoteReason;
      note: string;
      lines: { invoiceLineId: string; quantity: number }[];
    }) =>
      data.invoicing.createCreditNote(input.invoiceId, {
        reason: input.reason,
        note: input.note.trim() === "" ? undefined : input.note.trim(),
        lines: input.lines,
      }),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        // Write-through: the detail screen this hands off to renders
        // `result.data` as the channel returned it, never the drafted form.
        // Invalidated so the list this screen returns to (and the HSN
        // summary, which the same figures feed) refetch rather than trust a
        // locally patched row.
        void queryClient.invalidateQueries({
          queryKey: ["gst", "credit-notes"],
        });
        onCreated(result.data);
      } else {
        setRejection(result.rejection);
      }
    },
  });

  if (selectedInvoice === null) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-4">
        <div className="shrink-0">
          <Button size="sm" variant="ghost" onClick={onCancel}>
            <ArrowLeft />
            Back to credit notes
          </Button>
        </div>

        <div className="mx-auto w-full max-w-xl space-y-3">
          <div>
            <p className="font-medium">Find the invoice to credit</p>
            <p className="text-muted-foreground text-sm">
              A credit note can only be raised against an invoice that has been
              issued (FR-GST-16). Searching does not filter that out — picking a
              draft or cancelled invoice below still lets you try, and the write
              is refused with the reason shown.
            </p>
          </div>

          <div className="relative">
            <Search
              className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
              aria-hidden
            />
            <Input
              autoFocus
              placeholder="Invoice number or buyer name"
              className="pl-8"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          </div>

          {search.trim().length === 0 ? null : searchQuery.isPending ? (
            <p className="text-muted-foreground text-sm">Searching…</p>
          ) : searchQuery.isError ? (
            <p className="text-destructive text-sm">
              {searchQuery.error.message}
            </p>
          ) : searchQuery.data.items.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No invoice matches &ldquo;{search}&rdquo;.
            </p>
          ) : (
            <ul className="divide-y rounded-md border">
              {searchQuery.data.items.map((invoice) => (
                <li key={invoice.id}>
                  <button
                    type="button"
                    className="hover:bg-muted/50 flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm"
                    onClick={() => {
                      setSelectedInvoice(invoice);
                      setQuantities(fullQuantities(invoice));
                      setReason("");
                      setNote("");
                      setRejection(null);
                    }}
                  >
                    <span className="flex flex-col">
                      <span className="font-medium">
                        {invoice.invoiceNumber ?? "Not yet issued"}
                      </span>
                      <span className="text-muted-foreground">
                        {invoice.buyerName}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <InvoiceStatusBadge status={invoice.status} />
                      <span className="tabular-nums">
                        {formatMoney(invoice.total)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  const invoice = selectedInvoice;
  const isCgstSgst = invoice.taxKind === "cgst_sgst";
  const chosenReason = CREDIT_NOTE_REASONS.find(
    (entry) => entry.value === reason,
  );
  const canSubmit = chosenReason !== undefined && !mutation.isPending;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="shrink-0">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setSelectedInvoice(null);
            setRejection(null);
          }}
        >
          <ArrowLeft />
          Choose a different invoice
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-6 overflow-auto pb-8">
        <div className="flex items-center justify-between gap-3 rounded-md border p-3">
          <div>
            <p className="font-medium">
              {invoice.invoiceNumber ?? "Not yet issued"}
            </p>
            <p className="text-muted-foreground text-sm">
              {invoice.buyerName} · {formatMoney(invoice.total)}
            </p>
          </div>
          <InvoiceStatusBadge status={invoice.status} />
        </div>

        {invoice.status !== "issued" ? (
          <p className="text-destructive text-sm">
            This invoice is {invoice.status}. FR-GST-16 only allows a credit
            note against an issued invoice — submitting will come back blocked,
            but you can still see what that looks like.
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="credit-note-reason">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="credit-note-reason" className="w-full">
                <SelectValue placeholder="Choose a reason" />
              </SelectTrigger>
              <SelectContent>
                {CREDIT_NOTE_REASONS.map((entry) => (
                  <SelectItem key={entry.value} value={entry.value}>
                    {entry.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="credit-note-note">Note (optional)</Label>
            <Input
              id="credit-note-note"
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium">
            Tax type: {isCgstSgst ? "CGST + SGST" : "IGST"}
          </p>
          <p className="text-muted-foreground text-xs">
            Carried over from the invoice, not offered as a choice — this note
            reverses the specific charge {invoice.invoiceNumber ?? "above"}{" "}
            made, and today&apos;s place-of-supply rules have no say in what was
            actually charged then.
          </p>
        </div>

        <LinesTable
          invoice={invoice}
          isCgstSgst={isCgstSgst}
          quantities={quantities}
          onChangeQuantity={(lineId, quantity) => {
            setQuantities((current) => ({ ...current, [lineId]: quantity }));
          }}
        />

        <div className="flex items-center gap-3">
          <Button
            disabled={!canSubmit}
            onClick={() => {
              if (chosenReason === undefined) return;
              const lines = invoice.lines
                .map((line) => ({
                  invoiceLineId: line.id,
                  quantity: quantities[line.id] ?? 0,
                }))
                .filter((line) => line.quantity > 0);
              mutation.mutate({
                invoiceId: invoice.id,
                reason: chosenReason.value,
                note,
                lines,
              });
            }}
          >
            {mutation.isPending ? "Raising credit note…" : "Raise credit note"}
          </Button>
          {chosenReason === undefined ? (
            <p className="text-muted-foreground text-xs">
              Choose a reason to continue.
            </p>
          ) : null}
        </div>

        {rejection ? <RejectionNotice rejection={rejection} /> : null}
      </div>
    </div>
  );
}

function InvoiceStatusBadge({ status }: { status: Invoice["status"] }) {
  if (status === "issued") return <Badge>Issued</Badge>;
  if (status === "cancelled") {
    return <Badge variant="destructive">Cancelled</Badge>;
  }
  return <Badge variant="secondary">Draft</Badge>;
}

/**
 * One row per invoice line, with a credited-quantity input capped at the
 * line's original quantity.
 *
 * No line total is computed for a partial quantity: `taxableValue` and the
 * tax columns shown here are the invoice line's own stored figures, printed
 * as reference for the whole line, not recalculated pro rata for whatever
 * quantity is typed. Deriving a per-unit amount would mean dividing a
 * `Money` string, which is exactly the arithmetic this module avoids
 * everywhere else — the actual credited amounts are computed by the write
 * and shown afterwards on `CreditNoteDetail`, off the figures the channel
 * returns.
 */
function LinesTable({
  invoice,
  isCgstSgst,
  quantities,
  onChangeQuantity,
}: {
  invoice: Invoice;
  isCgstSgst: boolean;
  quantities: LineQuantities;
  onChangeQuantity: (lineId: string, quantity: number) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-muted/40">
          <tr className="border-b text-left">
            <th className="px-3 py-2 font-medium">Description</th>
            <th className="px-3 py-2 font-medium">HSN</th>
            <th className="px-3 py-2 text-right font-medium">Invoiced qty</th>
            <th className="px-3 py-2 text-right font-medium">Rate</th>
            {isCgstSgst ? (
              <>
                <th className="px-3 py-2 text-right font-medium">CGST</th>
                <th className="px-3 py-2 text-right font-medium">SGST</th>
              </>
            ) : (
              <th className="px-3 py-2 text-right font-medium">IGST</th>
            )}
            <th className="px-3 py-2 text-right font-medium">Line total</th>
            <th className="px-3 py-2 text-right font-medium">Credit qty</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((line) => (
            <tr key={line.id} className="border-b last:border-b-0">
              <td className="px-3 py-1.5">{line.description}</td>
              <td className="px-3 py-1.5 tabular-nums">{line.hsnCode}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {line.quantity}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {line.gstRate}%
              </td>
              {isCgstSgst ? (
                <>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatMoney(line.cgstAmount)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatMoney(line.sgstAmount)}
                  </td>
                </>
              ) : (
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {formatMoney(line.igstAmount)}
                </td>
              )}
              <td className="px-3 py-1.5 text-right tabular-nums">
                {formatMoney(line.lineTotal)}
              </td>
              <td className="px-3 py-1.5 text-right">
                <Input
                  type="number"
                  min={0}
                  max={line.quantity}
                  className="ml-auto h-8 w-20 text-right"
                  value={quantities[line.id] ?? 0}
                  onChange={(event) => {
                    const raw = Number(event.target.value);
                    const clamped = Number.isNaN(raw)
                      ? 0
                      : Math.min(Math.max(raw, 0), line.quantity);
                    onChangeQuantity(line.id, clamped);
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Every rejection kind rendered as what it actually is, not one generic
 * line — matching `HsnEditor`'s `RejectionNotice` in `modules/catalog`, but
 * as a true `switch` with no `default`: a rejection kind added to the union
 * in `@estelle/domain` fails this file at compile time instead of silently
 * falling through to nothing.
 *
 * `createCreditNote` takes no `Guarded`, so `conflict` cannot occur from
 * this action today, and `forbidden` would be caught by hiding the section
 * before this screen ever renders — but both still get their own line
 * rather than a fallthrough, for the reason above.
 */
function RejectionNotice({ rejection }: { rejection: WriteRejection }) {
  switch (rejection.kind) {
    // Both carry the channel's or the adapter's own words verbatim — this is
    // the case a draft or cancelled invoice hits (FR-GST-16).
    case "blocked":
    case "channel_rejected":
      return <p className="text-destructive text-sm">{rejection.message}</p>;
    case "invalid":
      return (
        <p className="text-destructive text-sm">
          {rejection.message}
          {rejection.fields
            ? " " +
              Object.entries(rejection.fields)
                .map(([field, problem]) => `${field}: ${problem}`)
                .join("; ")
            : null}
        </p>
      );
    case "forbidden":
      return (
        <p className="text-destructive text-sm">
          {rejection.message} (requires {rejection.permission})
        </p>
      );
    case "conflict":
      return (
        <p className="text-destructive text-sm">
          {rejection.message} It changed at {rejection.currentUpdatedAt}.
        </p>
      );
  }
}
